import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Loader2, ArrowLeft } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";

const ResetPasswordOtp = () => {
  const { resetPasswordWithOtp, requestResetOtp } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [passwordErr, setPasswordErr] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);

  useEffect(() => {
    const fromState = (location.state as { email?: string } | null)?.email;
    const fromStorage = sessionStorage.getItem("pendingResetEmail") || "";
    const picked = (fromState || fromStorage || "").trim().toLowerCase();
    setEmail(picked);
  }, [location.state]);

  const validatePassword = (val: string) =>
    val.length >= 8 && /[a-z]/.test(val) && /[A-Z]/.test(val) && /\d/.test(val);

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordErr("");

    if (!email) {
      toast({ title: "Missing email", description: "Start from the reset page again.", variant: "destructive" });
      navigate("/forgot-password", { replace: true });
      return;
    }

    if (code.length !== 6) {
      toast({ title: "Invalid Code", description: "Enter the 6-digit code from your email.", variant: "destructive" });
      return;
    }

    if (!validatePassword(password)) {
      setPasswordErr("Min 8 chars, include uppercase, lowercase and number");
      return;
    }

    setIsLoading(true);
    try {
      await resetPasswordWithOtp(email, code, password);
      sessionStorage.removeItem("pendingResetEmail");
      toast({ title: "Password Reset", description: "Login with your new password." });
      navigate("/login", { replace: true });
    } catch (err) {
      toast({ title: "Reset Failed", description: "Check your code and try again.", variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    if (!email) return;
    setIsResending(true);
    try {
      await requestResetOtp(email);
      toast({ title: "Code Sent", description: "We emailed you a new 6-digit code." });
    } finally {
      setIsResending(false);
    }
  };

  if (!email) {
    // Safety: if there’s no email context, send user back to start.
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl">Reset Password</CardTitle>
            <CardDescription>We need your email to send a code.</CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <Link to="/forgot-password">
              <Button>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Reset
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex items-center justify-center mb-2">
            <Link to="/forgot-password" className="absolute left-6 top-6">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
            </Link>
          </div>
          <CardTitle className="text-2xl">Enter Code</CardTitle>
          <CardDescription>
            We sent a 6-digit code to <span className="font-medium">{email}</span>
          </CardDescription>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleReset} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="code">6-digit Code</Label>
              <Input
                id="code"
                inputMode="numeric"
                maxLength={6}
                placeholder="● ● ● ● ● ●"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                disabled={isLoading}
                autoFocus
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">New Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="Enter new password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setPasswordErr("");
                }}
                disabled={isLoading}
                className={passwordErr ? "border-red-500" : ""}
              />
              {passwordErr && <p className="text-sm text-red-500">{passwordErr}</p>}
            </div>

            <Button type="submit" className="w-full" disabled={isLoading || code.length !== 6}>
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Resetting...
                </>
              ) : (
                "Reset Password"
              )}
            </Button>

            <div className="text-center">
              <Button type="button" variant="ghost" onClick={handleResend} disabled={isResending}>
                {isResending ? "Resending..." : "Resend Code"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPasswordOtp;
